'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksNedirPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-nedir-nasil-yapilir';
  const pageTitle = 'Botoks Nedir? Nasıl Yapılır? – Uygulama Aşamaları ve Etkileri';
  const pageDescription = 'Botoks nedir, nasıl uygulanır? Uygulama aşamaları, etkileri ve dikkat edilmesi gerekenler hakkında detaylı bilgiler.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks Nedir? Nasıl Yapılır?', href: '/tr/blog/botoks-nedir-nasil-yapilir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks uygulaması sırasında alın bölgesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Estetik Tedaviler
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoks Nedir? Nasıl Yapılır? – Uygulama Aşamaları ve Etkileri
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>15 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>10 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks yaptırmak isteyenler için en temel sorulardan biri: "Botoks tam olarak nedir ve nasıl uygulanır?" Bu yazıda botoksun tanımını, hangi amaçlarla kullanıldığını ve uygulama sürecini detaylıca ele aldık.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoks Nedir?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Botoks, botulinum toksini adlı maddenin estetik veya medikal amaçlarla kaslara enjekte edilmesiyle uygulanan bir tedavi yöntemidir. Bu madde, sinir iletisini geçici olarak engelleyerek kasın hareket etmesini durdurur. Sonuç olarak kırışıklıklar azalır veya tamamen kaybolur.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Hangi Amaçlarla Kullanılır?
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Alın ve kaş arası kırışıklıkların giderilmesi</li>
                  <li>Göz çevresi kaz ayaklarının silinmesi</li>
                  <li>Aşırı terleme (hiperhidroz) tedavisi</li>
                  <li>Migren ağrılarının azaltılması</li>
                  <li>Diş sıkma (bruksizm) kontrolü</li>
                  <li>Gummy smile (dişeti gülümsemesi) düzeltme</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoks Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks uygulaması genellikle 10–15 dakikalık bir işlemdir. Uygulama öncesinde özel bir hazırlığa gerek duyulmaz. Süreç şu adımlarla ilerler:
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Değerlendirme ve Planlama</h3>
                  <p className="text-gray-700">Hekim yüz kaslarını değerlendirir, mimik hareketlerine göre hangi bölgelere botoks yapılacağını belirler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Uygulama</h3>
                  <p className="text-gray-700">İnce uçlu iğnelerle hedef kaslara küçük dozlarda botoks enjekte edilir. İşlem sırasında genellikle ağrı hissedilmez, hafif bir batma duyulabilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Sonrası</h3>
                  <p className="text-gray-700">Uygulama sonrası kişi hemen günlük hayatına dönebilir. Etki süreci ise 3–7 gün içinde başlar.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoks Uygulama Bölgeleri
              </h2>

              {/* Application Areas Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Bölge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Amaç
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Alın</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yatay kırışıklıkların giderilmesi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Kaş arası</td>
                      <td className="px-6 py-4 text-sm text-gray-900">"Çatık kaş" görünümünün yumuşatılması</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Göz çevresi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kaz ayaklarının silinmesi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Üst dudak</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Sigara çizgilerinin azalması</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Burun kenarı ("bunny lines")</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Mimik kırışıklıklarının kontrolü</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Boyun (Nefertiti botoksu)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Boyun çizgilerinin azaltılması</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Koltuk altı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Aşırı terlemenin önlenmesi (medikal)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uygulama Öncesi ve Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Öncesi:</h3>
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Aspirin, E vitamini gibi kan sulandırıcı ilaçlar bırakılmalı</li>
                    <li>Alkol tüketimi azaltılmalı</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Sonrası:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>En az 4 saat boyunca yatılmamalı</li>
                    <li>İlk 24 saat ağır spor yapılmamalı</li>
                    <li>Masaj veya ovuşturma yapılmamalı</li>
                  </ul>
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">Botoks Kimlere Uygulanmaz?</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Hamile ve emziren kadınlar</li>
                    <li>Nörolojik hastalığı olanlar</li>
                    <li>Alerjisi olan kişiler</li>
                    <li>Uygulama yapılacak bölgede enfeksiyonu bulunanlar</li>
                  </ul>
                </div>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Botoks uygulaması sırasında alın bölgesi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Botoks acı verir mi?</h3>
                  <p className="text-gray-700">Hayır. Uygulama sırasında hafif bir batma dışında ağrı hissedilmez.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Botoks kalıcı mıdır?</h3>
                  <p className="text-gray-700">Hayır. Etkisi geçicidir. Genellikle 4–6 ay sonra yeniden uygulama gerekir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoks yüz ifadesini dondurur mu?</h3>
                  <p className="text-gray-700">Uygun doz ve doğru teknikle yapılan botoks, doğal bir görünüm sağlar. Aşırı dozda yapılan uygulamalar yüz ifadesini etkileyebilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>Mayo Clinic – <a href="https://www.mayoclinic.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.mayoclinic.org</a></li>
                <li>Türk Dermatoloji Derneği – <a href="https://www.turkderm.org.tr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.turkderm.org.tr</a></li>
                <li>Aesthetic Plastic Surgery Journal – 2023/Vol. 47</li>
                <li>Klinik Deneyimlere Dayalı 2024–2026 Hekim Görüşleri (İstanbul, İzmir, Ankara)</li>
              </ul>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Botoks Uygulaması İçin Randevu Alın</h3>
              <p className="mb-6">Uzman doktorumuzdan detaylı bilgi alın ve size özel değerlendirme için hemen iletişime geçin.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-fiyatlari-2026"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları 2026</h4>
              <p className="text-gray-600 text-sm">Güncel botoks fiyatları ve nelere göre değiştiği hakkında detaylı bilgiler.</p>
            </Link>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <Link
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 